// Types
export interface Link {
  id: string | null;
  customId: string | null;
  created: Date | null;
  updated: Date | null;
  redirectUrl: string | null;
  platform: string | null;
  linkName: string | null;
  adSetName: string | null;
  adName: string | null;
  metadata: Record<string, any> | null;
  forceExternalBrowser?: boolean;
  domain?: string;
  customQueryParams?: string;
  destinationType?: "single" | "ab-test" | "variant" | "custom";
  destinationPath?: string;
  destinationPathA?: string;
  destinationPathB?: string;
  abTestSplit?: number;
  destinationVariant?: string;
  customRedirectUrl?: string;
  abTestPaths?: Array<{
    id: string;
    type: "path" | "variant";
    value: string;
    percentage: number;
  }>;
  abTestSchedule?: {
    enabled: boolean;
    startDate?: string;
    endDate?: string;
  };
}

export interface CreateLinkData {
  redirectUrl: string;
  platform: string;
  linkName?: string;
  adSetName?: string;
  adName?: string;
  customId?: string;
  metadata?: Record<string, any>;
  forceExternalBrowser?: boolean;
  domain?: string;
  customQueryParams?: string;
  destinationType?: "single" | "ab-test" | "variant" | "custom";
  destinationPath?: string;
  destinationPathA?: string;
  destinationPathB?: string;
  abTestSplit?: number;
  destinationVariant?: string;
  customRedirectUrl?: string;
  abTestPaths?: Array<{
    id: string;
    type: "path" | "variant";
    value: string;
    percentage: number;
  }>;
  abTestSchedule?: {
    enabled: boolean;
    startDate?: string;
    endDate?: string;
  };
}

export interface Campaign {
  account_id: string | null;
  ad_name: string | null;
  ad_id: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  adset_id: string | null;
  adset_name: string | null;
  date_start: string | null;
  date_stop: string | null;
  created_time: string | null;
  cpc: string | null;
  cpm: string | null;
  cpp: string | null;
  ctr: string | null;
  frequency: string | null;
  impressions: string | null;
  spend: string | null;
  calendly_events?: number;
  calendly_com_events?: number;
  conversions?: number;
}

export interface CreateCampaignData {
  campaignName: string;
  adSetName: string;
  adName: string;
  platform: string;
  redirectUrl: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface User {
  id: number;
  email: string;
  name: string;
  token: string;
}

export interface ABTest {
  id: string;
  name: string;
  status: "active" | "paused" | "completed" | "draft";
  variants: number;
  trafficSplit: Array<{ name: string; percentage: number }>;
  conversionRate: number;
  totalVisitors: number;
  created: Date;
  updated: Date | null;
  isActive?: boolean;
  isWinner?: boolean;
}

export interface Path {
  id: string;
  path: string;
  githubRepo: string;
  githubSubfolder?: string;
  githubTag?: string;
  targetUrl?: string;
  status: "active" | "inactive";
  requestCount: number;
  lastAccessed: Date | null;
  created: Date;
  updated: Date | null;
  description?: string;
  metadata?: Record<string, any>;
  forceExternalBrowser?: boolean;
  customQueryParams?: string;
  parameters?: Array<{
    name: string;
    required: boolean;
    defaultValue?: string;
  }>;
}

export interface PreconnectEntry {
  id: string;
  url: string;
  type: "dns-prefetch" | "preconnect";
  status: "active" | "inactive";
  description?: string;
  paths?: string[];
  created: Date;
  updated: Date | null;
  lastUsed: Date | null;
  usageCount: number;
  performance?: {
    avgTimeSaved?: number;
    successRate?: number;
  };
}

// Mock data (mutable for create/update/delete)
const mockLinks: Link[] = [
  {
    id: "camp_001",
    customId: "summer-sale-2024",
    created: new Date("2025-11-01T10:30:00Z"),
    updated: new Date("2025-11-08T14:45:00Z"),
    redirectUrl: null,
    platform: "Google Ads",
    linkName: "Summer Sale 2024",
    adSetName: "Young Adults 18-35",
    adName: "Beach Vacation Deal",
    domain: "try.zown.ca",
    destinationType: "single",
    destinationPath: "/buyer/home",
    forceExternalBrowser: true,
    customQueryParams: "utm_source=google&utm_medium=cpc",
    metadata: {
      title: "Summer Sale 2024 - 50% Off Beach Vacations",
      description: "Limited time offer on all beach destinations",
      customConfig: {
        showBanner: true,
        bannerColor: "#ff6b6b",
        countdown: true
      }
    }
  },
  {
    id: "camp_002",
    customId: null,
    created: new Date("2025-10-18T09:15:00Z"),
    updated: new Date("2025-11-05T16:20:00Z"),
    redirectUrl: null,
    platform: "Facebook Ads",
    linkName: "New Product Launch",
    adSetName: "Tech Enthusiasts",
    adName: "Innovation Spotlight",
    domain: "go.zown.ca",
    destinationType: "ab-test",
    abTestPaths: [
      { id: "1", type: "path", value: "/seller/home", percentage: 30 },
      { id: "2", type: "path", value: "/seller/valuation", percentage: 40 },
      { id: "3", type: "variant", value: "54424264261", percentage: 30 }
    ],
    abTestSchedule: {
      enabled: true,
      startDate: "2025-11-15T08:00:00",
      endDate: "2025-12-15T20:00:00"
    },
    metadata: null
  },
  {
    id: "camp_003",
    customId: "holiday-promo",
    created: new Date("2025-10-10T11:00:00Z"),
    updated: new Date("2025-10-25T13:30:00Z"),
    redirectUrl: null,
    platform: "X Ads (Twitter)",
    linkName: null,
    adSetName: null,
    adName: "Festive Special Offer",
    domain: "try.zownhomes.com",
    destinationType: "variant",
    destinationVariant: "1754424264261",
    forceExternalBrowser: false,
    metadata: null
  },
  {
    id: "camp_004",
    customId: "b2b-leads",
    created: new Date("2025-10-22T08:45:00Z"),
    updated: null,
    redirectUrl: null,
    platform: "LinkedIn Ads",
    linkName: "B2B Lead Generation",
    adSetName: null,
    adName: null,
    domain: "go.zownhomes.com",
    destinationType: "single",
    destinationPath: "/agent/dashboard",
    customQueryParams: "ref=linkedin&campaign=b2b",
    metadata: null
  },
  {
    id: null,
    customId: null,
    created: new Date("2025-10-25T15:20:00Z"),
    updated: new Date("2025-10-26T10:10:00Z"),
    redirectUrl: "https://example.com/brand-awareness",
    platform: "Custom",
    linkName: null,
    adSetName: null,
    adName: null,
    metadata: null
  },
  {
    id: "camp_005",
    customId: "spring-2024",
    created: new Date("2025-10-28T14:20:00Z"),
    updated: new Date("2025-10-01T09:30:00Z"),
    redirectUrl: null,
    platform: "Instagram Ads",
    linkName: "Spring Collection 2024",
    adSetName: "Fashion Lovers 25-45",
    adName: "New Arrivals Showcase",
    domain: "try.zown.ca",
    destinationType: "ab-test",
    abTestPaths: [
      { id: "1", type: "path", value: "/buyer/search", percentage: 50 },
      { id: "2", type: "path", value: "/buyer/favorites", percentage: 50 }
    ],
    abTestSchedule: {
      enabled: false,
      startDate: "",
      endDate: ""
    },
    forceExternalBrowser: true,
    metadata: null
  },
  {
    id: "camp_006",
    customId: null,
    created: new Date("2025-10-02T11:15:00Z"),
    updated: null,
    redirectUrl: null,
    platform: "TikTok Ads",
    linkName: "Mobile App Download",
    adSetName: "Gen Z Users",
    adName: "App Store Feature",
    domain: "go.zown.ca",
    destinationType: "single",
    destinationPath: "/resources",
    customQueryParams: "source=tiktok&gen=z",
    metadata: null
  },
  {
    id: "camp_007",
    customId: null,
    created: new Date("2025-10-05T16:45:00Z"),
    updated: new Date("2025-10-08T12:20:00Z"),
    redirectUrl: null,
    platform: "LinkedIn Ads",
    linkName: "Professional Webinar Series",
    adSetName: "C-Level Executives",
    adName: "Leadership Insights",
    domain: "try.zownhomes.com",
    destinationType: "variant",
    destinationVariant: "2854424264261",
    metadata: null
  },
  {
    id: "camp_008",
    customId: null,
    created: new Date("2025-10-10T08:30:00Z"),
    updated: new Date("2025-10-12T15:10:00Z"),
    redirectUrl: null,
    platform: "Facebook Ads",
    linkName: "Valentine's Day Special",
    adSetName: "Couples 20-40",
    adName: "Romantic Getaway",
    domain: "go.zownhomes.com",
    destinationType: "single",
    destinationPath: "/contact",
    forceExternalBrowser: false,
    metadata: null
  },
  {
    id: "camp_009",
    customId: null,
    created: new Date("2025-10-15T13:20:00Z"),
    updated: null,
    redirectUrl: "https://example.com/fitness-challenge",
    platform: "Instagram Ads",
    linkName: "30-Day Fitness Challenge",
    adSetName: "Health Enthusiasts",
    adName: "Transform Your Body",
    domain: "try.zown.ca",
    destinationType: "custom",
    customRedirectUrl: "https://example.com/fitness-challenge",
    metadata: null
  },
  {
    id: "camp_010",
    customId: null,
    created: new Date("2025-10-18T10:05:00Z"),
    updated: new Date("2025-10-20T14:35:00Z"),
    redirectUrl: "https://example.com/tax-software",
    platform: "Google Ads",
    linkName: "Tax Season Helper",
    adSetName: "Small Business Owners",
    adName: "Easy Tax Filing",
    domain: "go.zownhomes.com",
    destinationType: "custom",
    customRedirectUrl: "https://example.com/tax-software",
    forceExternalBrowser: false,
    metadata: null
  },
  {
    id: "camp_011",
    customId: null,
    created: new Date("2025-10-22T09:40:00Z"),
    updated: null,
    redirectUrl: "https://example.com/book-launch",
    platform: "X Ads (Twitter)",
    linkName: "Bestseller Book Launch",
    adSetName: "Book Readers",
    adName: "Pre-Order Now",
    domain: "try.zownhomes.com",
    destinationType: "custom",
    customRedirectUrl: "https://example.com/book-launch",
    metadata: null
  },
  {
    id: "camp_012",
    customId: null,
    created: new Date("2025-10-25T15:55:00Z"),
    updated: new Date("2025-10-28T11:25:00Z"),
    redirectUrl: "https://example.com/conference-2024",
    platform: "LinkedIn Ads",
    linkName: "Tech Conference 2024",
    adSetName: "Software Engineers",
    adName: "Early Bird Tickets",
    domain: "go.zown.ca",
    destinationType: "custom",
    customRedirectUrl: "https://example.com/conference-2024",
    metadata: null
  },
  {
    id: "camp_013",
    customId: null,
    created: new Date("2025-11-01T12:10:00Z"),
    updated: null,
    redirectUrl: "https://example.com/kitchen-appliances",
    platform: "Facebook Ads",
    linkName: "Smart Kitchen Revolution",
    adSetName: "Home Cooks 30-55",
    adName: "Modern Appliances",
    domain: "try.zownhomes.com",
    destinationType: "custom",
    customRedirectUrl: "https://example.com/kitchen-appliances",
    metadata: null
  },
  {
    id: "camp_014",
    customId: null,
    created: new Date("2025-11-05T07:30:00Z"),
    updated: new Date("2025-11-08T16:45:00Z"),
    redirectUrl: "https://example.com/gaming-tournament",
    platform: "TikTok Ads",
    linkName: "Ultimate Gaming Tournament",
    adSetName: "Gamers 16-30",
    adName: "Join the Battle",
    domain: "go.zownhomes.com",
    destinationType: "custom",
    customRedirectUrl: "https://example.com/gaming-tournament",
    forceExternalBrowser: true,
    metadata: null
  },
  {
    id: "camp_015",
    customId: null,
    created: new Date("2025-11-10T14:20:00Z"),
    updated: null,
    redirectUrl: "https://example.com/investment-guide",
    platform: "LinkedIn Ads",
    linkName: "Investment Masterclass",
    adSetName: "Financial Professionals",
    adName: "Wealth Building Strategies",
    domain: "try.zown.ca",
    destinationType: "custom",
    customRedirectUrl: "https://example.com/investment-guide",
    metadata: null
  },
  {
    id: "camp_016",
    customId: null,
    created: new Date("2025-11-12T11:35:00Z"),
    updated: new Date("2025-11-15T09:20:00Z"),
    redirectUrl: "https://example.com/eco-products",
    platform: "Instagram Ads",
    linkName: "Sustainable Living",
    adSetName: "Eco-Conscious Consumers",
    adName: "Green Products Line",
    domain: "go.zown.ca",
    destinationType: "custom",
    customRedirectUrl: "https://example.com/eco-products",
    metadata: null
  },
  {
    id: "camp_017",
    customId: null,
    created: new Date("2025-11-18T16:00:00Z"),
    updated: null,
    redirectUrl: "https://example.com/music-festival",
    platform: "X Ads (Twitter)",
    linkName: "Summer Music Festival",
    adSetName: "Music Lovers 18-40",
    adName: "Festival Lineup Reveal",
    domain: "try.zownhomes.com",
    destinationType: "custom",
    customRedirectUrl: "https://example.com/music-festival",
    metadata: null
  },
  {
    id: "camp_018",
    customId: null,
    created: new Date("2025-11-20T08:15:00Z"),
    updated: new Date("2025-11-22T13:50:00Z"),
    redirectUrl: "https://example.com/pet-supplies",
    platform: "Google Ads",
    linkName: "Pet Care Essentials",
    adSetName: "Pet Owners",
    adName: "Premium Pet Products",
    domain: "go.zownhomes.com",
    destinationType: "custom",
    customRedirectUrl: "https://example.com/pet-supplies",
    metadata: null
  },
  {
    id: "camp_019",
    customId: null,
    created: new Date("2025-11-25T10:25:00Z"),
    updated: null,
    redirectUrl: "https://example.com/language-course",
    platform: "Facebook Ads",
    linkName: "Learn Spanish Fast",
    adSetName: "Language Learners",
    adName: "Fluent in 3 Months",
    domain: "try.zown.ca",
    destinationType: "custom",
    customRedirectUrl: "https://example.com/language-course",
    metadata: null
  },
  {
    id: "camp_020",
    customId: null,
    created: new Date("2025-11-28T15:40:00Z"),
    updated: new Date("2025-11-30T12:15:00Z"),
    redirectUrl: "https://example.com/travel-deals",
    platform: "Instagram Ads",
    linkName: "Spring Break Destinations",
    adSetName: "College Students",
    adName: "Beach Paradise Deals",
    domain: "go.zownhomes.com",
    destinationType: "custom",
    customRedirectUrl: "https://example.com/travel-deals",
    metadata: null
  },
  {
    id: "camp_021",
    customId: null,
    created: new Date("2025-11-01T09:10:00Z"),
    updated: null,
    redirectUrl: "https://example.com/home-security",
    platform: "Google Ads",
    linkName: "Smart Home Security",
    adSetName: "Homeowners 35-65",
    adName: "Protect Your Family",
    domain: "try.zownhomes.com",
    destinationType: "custom",
    customRedirectUrl: "https://example.com/home-security",
    forceExternalBrowser: false,
    metadata: null
  },
  {
    id: "camp_022",
    customId: null,
    created: new Date("2025-11-05T14:55:00Z"),
    updated: new Date("2025-11-08T11:30:00Z"),
    redirectUrl: "https://example.com/art-supplies",
    platform: "TikTok Ads",
    linkName: "Creative Art Studio",
    adSetName: "Artists & Creators",
    adName: "Professional Art Tools",
    domain: "go.zown.ca",
    destinationType: "custom",
    customRedirectUrl: "https://example.com/art-supplies",
    metadata: null
  },
  {
    id: "camp_023",
    customId: null,
    created: new Date("2025-11-10T13:25:00Z"),
    updated: null,
    redirectUrl: "https://example.com/career-coaching",
    platform: "LinkedIn Ads",
    linkName: "Career Advancement Program",
    adSetName: "Mid-Level Professionals",
    adName: "Next Level Success",
    domain: "try.zown.ca",
    destinationType: "custom",
    customRedirectUrl: "https://example.com/career-coaching",
    metadata: null
  },
  {
    id: "camp_024",
    customId: null,
    created: new Date("2025-11-12T16:30:00Z"),
    updated: new Date("2025-11-15T10:45:00Z"),
    redirectUrl: "https://example.com/wedding-planning",
    platform: "Facebook Ads",
    linkName: "Dream Wedding Planning",
    adSetName: "Engaged Couples",
    adName: "Perfect Wedding Day",
    domain: "go.zownhomes.com",
    destinationType: "custom",
    customRedirectUrl: "https://example.com/wedding-planning",
    metadata: null
  },
  {
    id: "camp_025",
    customId: null,
    created: new Date("2025-11-18T12:05:00Z"),
    updated: null,
    redirectUrl: "https://example.com/gardening-tools",
    platform: "Instagram Ads",
    linkName: "Spring Gardening Guide",
    adSetName: "Garden Enthusiasts",
    adName: "Bloom This Season",
    domain: "try.zown.ca",
    destinationType: "custom",
    customRedirectUrl: "https://example.com/gardening-tools",
    metadata: null
  },
  {
    id: "camp_026",
    customId: null,
    created: new Date("2025-11-20T08:50:00Z"),
    updated: new Date("2025-11-22T15:20:00Z"),
    redirectUrl: "https://example.com/coding-bootcamp",
    platform: "X Ads (Twitter)",
    linkName: "Full-Stack Developer Bootcamp",
    adSetName: "Career Changers",
    adName: "Code Your Future",
    domain: "try.zownhomes.com",
    destinationType: "custom",
    customRedirectUrl: "https://example.com/coding-bootcamp",
    metadata: null
  },
  {
    id: "camp_027",
    customId: null,
    created: new Date("2025-11-25T11:40:00Z"),
    updated: null,
    redirectUrl: "https://example.com/meditation-app",
    platform: "Google Ads",
    linkName: "Mindfulness & Meditation",
    adSetName: "Stressed Professionals",
    adName: "Find Inner Peace",
    domain: "go.zown.ca",
    destinationType: "custom",
    customRedirectUrl: "https://example.com/meditation-app",
    metadata: null
  },
  {
    id: "camp_028",
    customId: null,
    created: new Date("2025-11-28T14:15:00Z"),
    updated: new Date("2025-11-01T09:35:00Z"),
    redirectUrl: "https://example.com/craft-brewery",
    platform: "Facebook Ads",
    linkName: "Craft Beer Tasting",
    adSetName: "Beer Enthusiasts 25-50",
    adName: "Local Brewery Tour",
    domain: "try.zown.ca",
    destinationType: "custom",
    customRedirectUrl: "https://example.com/craft-brewery",
    metadata: null
  },
  {
    id: "camp_029",
    customId: null,
    created: new Date("2025-11-03T10:20:00Z"),
    updated: null,
    redirectUrl: "https://example.com/photography-course",
    platform: "Instagram Ads",
    linkName: "Master Photography Skills",
    adSetName: "Aspiring Photographers",
    adName: "Capture Perfect Moments",
    domain: "go.zownhomes.com",
    destinationType: "custom",
    customRedirectUrl: "https://example.com/photography-course",
    metadata: null
  },
  {
    id: "camp_030",
    customId: null,
    created: new Date("2025-11-06T16:25:00Z"),
    updated: new Date("2025-11-08T13:10:00Z"),
    redirectUrl: "https://example.com/skincare-routine",
    platform: "TikTok Ads",
    linkName: "Glowing Skin Secrets",
    adSetName: "Beauty Enthusiasts",
    adName: "Radiant Complexion",
    domain: "try.zownhomes.com",
    destinationType: "custom",
    customRedirectUrl: "https://example.com/skincare-routine",
    metadata: null
  },
  {
    id: "book",
    customId: "book-consultation",
    created: new Date("2025-11-10T10:00:00Z"),
    updated: new Date("2025-11-10T14:30:00Z"),
    redirectUrl: "https://calendly.com/zown/consultation",
    platform: "Custom",
    linkName: "Book Consultation",
    adSetName: "Direct Booking",
    adName: "Schedule Meeting",
    domain: "go.zown.ca",
    destinationType: "custom",
    customRedirectUrl: "https://calendly.com/zown/consultation",
    metadata: null
  },
  {
    id: "meet",
    customId: null,
    created: new Date("2025-11-11T09:15:00Z"),
    updated: null,
    redirectUrl: "https://zoom.us/meeting/12345",
    platform: "Custom",
    linkName: "Virtual Meeting",
    adSetName: "Online Sessions",
    adName: "Meet with Expert",
    domain: "try.zown.ca",
    destinationType: "custom",
    customRedirectUrl: "https://zoom.us/meeting/12345",
    metadata: null
  },
  {
    id: "234234324",
    customId: null,
    created: new Date("2025-11-12T11:30:00Z"),
    updated: new Date("2025-11-13T08:45:00Z"),
    redirectUrl: "https://example.com/special-offer",
    platform: "Google Ads",
    linkName: "Special Offer Campaign",
    adSetName: "Limited Time",
    adName: "Flash Sale",
    domain: "go.zownhomes.com",
    destinationType: "custom",
    customRedirectUrl: "https://example.com/special-offer",
    metadata: null
  },
  {
    id: "43534534253452",
    customId: null,
    created: new Date("2025-11-14T14:20:00Z"),
    updated: null,
    redirectUrl: "https://example.com/new-product",
    platform: "Facebook Ads",
    linkName: "Product Launch 2024",
    adSetName: "Early Adopters",
    adName: "Be First to Try",
    domain: "try.zownhomes.com",
    destinationType: "custom",
    customRedirectUrl: "https://example.com/new-product",
    metadata: null
  },
  {
    id: "52453452345",
    customId: null,
    created: new Date("2025-11-15T10:00:00Z"),
    updated: null,
    redirectUrl: "https://example.com/summer-promo",
    platform: "Custom",
    linkName: "Summer Promotion",
    adSetName: "Seasonal Campaign",
    adName: "Hot Summer Deals",
    metadata: null
  },
  {
    id: "4256245624563",
    customId: null,
    created: new Date("2025-11-16T11:30:00Z"),
    updated: new Date("2025-11-17T09:15:00Z"),
    redirectUrl: "https://example.com/webinar",
    platform: "Custom",
    linkName: "Webinar Registration",
    adSetName: "Educational Content",
    adName: "Learn From Experts",
    metadata: null
  },
  {
    id: "234523452",
    customId: null,
    created: new Date("2025-11-18T14:45:00Z"),
    updated: null,
    redirectUrl: "https://example.com/consultation",
    platform: "Custom",
    linkName: "Free Consultation",
    adSetName: "Lead Generation",
    adName: "Book Your Session",
    metadata: null
  },
  {
    id: "4256245624562546",
    customId: null,
    created: new Date("2025-11-19T08:20:00Z"),
    updated: new Date("2025-11-20T16:30:00Z"),
    redirectUrl: "https://example.com/trial",
    platform: "Custom",
    linkName: "Free Trial Offer",
    adSetName: "Conversion Campaign",
    adName: "Try Before You Buy",
    metadata: null
  },
  {
    id: "4256245624562",
    customId: null,
    created: new Date("2025-11-21T13:00:00Z"),
    updated: null,
    redirectUrl: "https://example.com/demo",
    platform: "Custom",
    linkName: "Product Demo",
    adSetName: "Demo Campaign",
    adName: "See It In Action",
    metadata: null
  }
];

// A/B Test mock data
const mockABTests: ABTest[] = [
  {
    id: "test_001",
    name: "Homepage Hero Test",
    status: "active",
    variants: 2,
    trafficSplit: [
      { name: "Control", percentage: 50 },
      { name: "Variant A", percentage: 50 }
    ],
    conversionRate: 3.5,
    totalVisitors: 15420,
    created: new Date("2025-10-15T10:00:00Z"),
    updated: new Date("2025-10-20T14:30:00Z"),
    isActive: true,
    isWinner: false
  },
  {
    id: "test_002",
    name: "Checkout Flow Optimization",
    status: "completed",
    variants: 3,
    trafficSplit: [
      { name: "Control", percentage: 33 },
      { name: "Variant A", percentage: 33 },
      { name: "Variant B", percentage: 34 }
    ],
    conversionRate: 5.2,
    totalVisitors: 28350,
    created: new Date("2025-10-10T09:00:00Z"),
    updated: new Date("2025-10-25T16:45:00Z"),
    isActive: false,
    isWinner: true
  },
  {
    id: "test_003",
    name: "Pricing Page Layout",
    status: "paused",
    variants: 2,
    trafficSplit: [
      { name: "Control", percentage: 60 },
      { name: "Variant A", percentage: 40 }
    ],
    conversionRate: 2.8,
    totalVisitors: 8920,
    created: new Date("2025-10-18T11:30:00Z"),
    updated: new Date("2025-10-22T10:15:00Z"),
    isActive: false,
    isWinner: false
  },
  {
    id: "test_004",
    name: "CTA Button Color Test",
    status: "active",
    variants: 4,
    trafficSplit: [
      { name: "Control", percentage: 25 },
      { name: "Blue", percentage: 25 },
      { name: "Green", percentage: 25 },
      { name: "Orange", percentage: 25 }
    ],
    conversionRate: 4.1,
    totalVisitors: 12650,
    created: new Date("2025-10-20T08:00:00Z"),
    updated: new Date("2025-10-23T15:20:00Z"),
    isActive: true,
    isWinner: false
  },
  {
    id: "test_005",
    name: "Landing Page Headlines",
    status: "draft",
    variants: 2,
    trafficSplit: [
      { name: "Control", percentage: 50 },
      { name: "Variant A", percentage: 50 }
    ],
    conversionRate: 0,
    totalVisitors: 0,
    created: new Date("2025-10-24T10:00:00Z"),
    updated: null,
    isActive: false,
    isWinner: false
  }
];

// Path mock data
const mockPaths: Path[] = [
  {
    id: "vpath_001",
    path: "/promo/summer-sale",
    githubRepo: "example/marketing-site",
    githubSubfolder: "campaigns/summer",
    githubTag: "v2.1.0",
    targetUrl: "https://example.com/campaigns/summer-2024",
    status: "active",
    requestCount: 12543,
    lastAccessed: new Date("2025-10-10T15:30:00Z"),
    created: new Date("2025-10-01T10:00:00Z"),
    updated: new Date("2025-10-01T14:00:00Z"),
    description: "Summer sale campaign landing page",
    parameters: [
      { name: "source", required: false, defaultValue: "direct" },
      { name: "utm_campaign", required: false }
    ]
  },
  {
    id: "vpath_002",
    path: "/go/webinar",
    githubRepo: "example/webinar-platform",
    githubTag: "latest",
    targetUrl: "https://webinar.example.com/register",
    status: "active",
    requestCount: 8932,
    lastAccessed: new Date("2025-10-10T14:45:00Z"),
    created: new Date("2025-10-15T09:00:00Z"),
    updated: null,
    description: "Webinar registration redirect"
  },
  {
    id: "vpath_003",
    path: "/docs/api",
    githubRepo: "example/documentation",
    githubSubfolder: "api/v2",
    githubTag: "v2.0.0",
    targetUrl: "https://docs.example.com/api/v2",
    status: "active",
    requestCount: 45678,
    lastAccessed: new Date("2025-10-10T16:00:00Z"),
    created: new Date("2025-12-01T08:00:00Z"),
    updated: new Date("2025-10-20T11:30:00Z"),
    description: "API documentation redirect"
  },
  {
    id: "vpath_004",
    path: "/app/download",
    githubRepo: "example/mobile-app",
    githubSubfolder: "download",
    githubTag: "v1.5.0",
    targetUrl: "https://apps.example.com/download/latest",
    status: "inactive",
    requestCount: 3421,
    lastAccessed: new Date("2025-10-28T10:20:00Z"),
    created: new Date("2025-11-15T12:00:00Z"),
    updated: new Date("2025-10-28T10:30:00Z"),
    description: "Mobile app download page (deprecated)"
  },
  {
    id: "vpath_005",
    path: "/partner/affiliate",
    githubRepo: "example/partner-portal",
    githubSubfolder: "affiliate",
    githubTag: "main",
    targetUrl: "https://partners.example.com/affiliate-program",
    status: "active",
    requestCount: 6789,
    lastAccessed: new Date("2025-10-10T13:15:00Z"),
    created: new Date("2025-10-05T14:00:00Z"),
    updated: null,
    description: "Affiliate program signup",
    parameters: [
      { name: "ref", required: true },
      { name: "campaign", required: false, defaultValue: "general" }
    ]
  },
  {
    id: "vpath_006",
    path: "/event/conference2024",
    githubRepo: "example/events-site",
    githubSubfolder: "conferences/2024",
    githubTag: "conference-2024",
    targetUrl: "https://events.example.com/annual-conference-2024",
    status: "active",
    requestCount: 15234,
    lastAccessed: new Date("2025-10-10T16:30:00Z"),
    created: new Date("2025-10-10T10:00:00Z"),
    updated: new Date("2025-10-05T09:00:00Z"),
    description: "Annual conference 2024 registration"
  },
  {
    id: "vpath_007",
    path: "/support/help",
    githubRepo: "example/support-center",
    githubTag: "stable",
    targetUrl: "https://support.example.com/help-center",
    status: "active",
    requestCount: 98765,
    lastAccessed: new Date("2025-10-10T16:45:00Z"),
    created: new Date("2025-10-01T08:00:00Z"),
    updated: new Date("2025-10-15T12:00:00Z"),
    description: "Help center redirect"
  },
  {
    id: "vpath_008",
    path: "/blog/latest",
    githubRepo: "example/blog",
    githubSubfolder: "posts/latest",
    githubTag: "main",
    targetUrl: "https://blog.example.com/latest-posts",
    status: "active",
    requestCount: 23456,
    lastAccessed: new Date("2025-10-10T15:50:00Z"),
    created: new Date("2025-12-15T11:00:00Z"),
    updated: null,
    description: "Latest blog posts redirect"
  }
];

// Mock Preconnect data
const mockPreconnectEntries: PreconnectEntry[] = [
  {
    id: "preconnect_001",
    url: "https://fonts.googleapis.com",
    type: "preconnect",
    status: "active",
    description: "Google Fonts preconnection for faster font loading",
    paths: ["/promo/summer-sale", "/products/new-arrivals"],
    created: new Date("2025-10-15T10:00:00Z"),
    updated: new Date("2025-10-01T14:00:00Z"),
    lastUsed: new Date("2025-10-15T09:30:00Z"),
    usageCount: 45678,
    performance: {
      avgTimeSaved: 125,
      successRate: 98.5
    }
  },
  {
    id: "preconnect_002",
    url: "https://fonts.gstatic.com",
    type: "preconnect",
    status: "active",
    description: "Google Fonts static resources",
    paths: ["/promo/summer-sale", "/products/new-arrivals"],
    created: new Date("2025-10-15T10:00:00Z"),
    updated: new Date("2025-10-01T14:00:00Z"),
    lastUsed: new Date("2025-10-15T09:35:00Z"),
    usageCount: 45678,
    performance: {
      avgTimeSaved: 110,
      successRate: 99.2
    }
  },
  {
    id: "preconnect_003",
    url: "https://cdn.segment.com",
    type: "dns-prefetch",
    status: "active",
    description: "Segment analytics DNS prefetch",
    paths: ["*"],
    created: new Date("2025-10-20T12:00:00Z"),
    updated: new Date("2025-10-05T16:00:00Z"),
    lastUsed: new Date("2025-10-15T09:40:00Z"),
    usageCount: 89234,
    performance: {
      avgTimeSaved: 75,
      successRate: 97.8
    }
  },
  {
    id: "preconnect_004",
    url: "https://www.google-analytics.com",
    type: "dns-prefetch",
    status: "active",
    description: "Google Analytics DNS prefetch",
    paths: ["*"],
    created: new Date("2025-10-10T08:00:00Z"),
    updated: new Date("2025-10-08T11:00:00Z"),
    lastUsed: new Date("2025-10-15T09:45:00Z"),
    usageCount: 123456,
    performance: {
      avgTimeSaved: 85,
      successRate: 99.1
    }
  },
  {
    id: "preconnect_005",
    url: "https://cdn.amplitude.com",
    type: "preconnect",
    status: "inactive",
    description: "Amplitude analytics preconnection",
    paths: ["/checkout", "/payment"],
    created: new Date("2025-10-25T14:00:00Z"),
    updated: new Date("2025-10-10T10:00:00Z"),
    lastUsed: new Date("2025-10-12T15:00:00Z"),
    usageCount: 34567,
    performance: {
      avgTimeSaved: 95,
      successRate: 96.5
    }
  },
  {
    id: "preconnect_006",
    url: "https://api.stripe.com",
    type: "preconnect",
    status: "active",
    description: "Stripe payment API preconnection for checkout pages",
    paths: ["/checkout", "/payment", "/subscription"],
    created: new Date("2025-10-12T09:00:00Z"),
    updated: new Date("2025-10-03T13:00:00Z"),
    lastUsed: new Date("2025-10-15T08:20:00Z"),
    usageCount: 67890,
    performance: {
      avgTimeSaved: 145,
      successRate: 99.5
    }
  }
];

// Mock Scripts data
const mockScripts: any[] = [
  {
    id: "script_001",
    name: "Google Analytics 4",
    description: "Google Analytics 4 tracking code for website analytics",
    placement: "head",
    type: "script",
    content: `<!-- Google Analytics -->
<script async src="https://www.googletagmanager.com/gtag/js?id=GA_MEASUREMENT_ID"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', 'GA_MEASUREMENT_ID');
</script>`,
    status: "active",
    usageCount: 3,
    lastModified: "Feb 10, 2024",
    createdBy: "Admin User",
    paths: ["vpath_001", "vpath_002", "vpath_003"]
  },
  {
    id: "script_002",
    name: "Facebook Pixel",
    description: "Facebook Pixel tracking for conversions and retargeting",
    placement: "head",
    type: "script",
    content: `<!-- Facebook Pixel Code -->
<script>
  !function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t,s)}(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');
  fbq('init', 'YOUR_PIXEL_ID');
  fbq('track', 'PageView');
</script>`,
    status: "active",
    usageCount: 2,
    lastModified: "Feb 8, 2024",
    createdBy: "Marketing Team",
    paths: ["vpath_001", "vpath_006"]
  },
  {
    id: "script_003",
    name: "SEO Meta Tags",
    description: "Standard SEO meta tags for better search engine visibility",
    placement: "head",
    type: "meta",
    content: `<!-- SEO Meta Tags -->
<meta name="description" content="{{page_description}}">
<meta name="keywords" content="{{page_keywords}}">
<meta name="author" content="{{company_name}}">
<meta property="og:title" content="{{page_title}}">
<meta property="og:description" content="{{page_description}}">
<meta property="og:image" content="{{page_image}}">
<meta property="og:url" content="{{page_url}}">
<meta name="twitter:card" content="summary_large_image">`,
    status: "inactive",
    usageCount: 0,
    lastModified: "Feb 5, 2024",
    createdBy: "SEO Team",
    paths: []
  }
];

// Campaign mock data - Canadian Real Estate (matches new dataset structure)
const mockCampaigns: Campaign[] = [
  {
    account_id: "91396945",
    ad_name: "Downtown Toronto Condos",
    ad_id: "6817650353530",
    campaign_id: "6769443126530",
    campaign_name: "AD_CA_Toronto_Condos_FirstTimeBuyers_11.01.2025",
    adset_id: "6817650353730",
    adset_name: "First-Time Buyers 25-40",
    date_start: "2025-11-01",
    date_stop: "2025-11-10",
    created_time: "2025-10-28",
    cpc: "3.25",
    cpm: "42.50",
    cpp: "45.20",
    ctr: "0.76",
    frequency: "1.15",
    impressions: "2850",
    spend: "285.50",
    calendly_events: 24,
    calendly_com_events: 15,
    conversions: 12
  },
  {
    account_id: "91396946",
    ad_name: "GTA Home Buyers Guide",
    ad_id: "6817650353531",
    campaign_id: "6769443126531",
    campaign_name: "AD_CA_GTA_HomeBuyers_Fall_10.15.2025",
    adset_id: "6817650353731",
    adset_name: "Interest - Home Ownership",
    date_start: "2025-10-15",
    date_stop: "2025-11-15",
    created_time: "2025-10-10",
    cpc: "2.85",
    cpm: "38.20",
    cpp: "40.15",
    ctr: "0.82",
    frequency: "1.28",
    impressions: "3420",
    spend: "420.75",
    calendly_events: 32,
    calendly_com_events: 18,
    conversions: 18
  },
  {
    account_id: "91396947",
    ad_name: "Mississauga New Listings",
    ad_id: "6817650353532",
    campaign_id: "6769443126532",
    campaign_name: "AD_CA_Mississauga_NewListings_10.20.2025",
    adset_id: "6817650353732",
    adset_name: "Retargeting - Website Visitors",
    date_start: "2025-10-20",
    date_stop: "2025-11-05",
    created_time: "2025-10-18",
    cpc: "2.45",
    cpm: "35.80",
    cpp: "37.90",
    ctr: "0.68",
    frequency: "1.22",
    impressions: "2680",
    spend: "195.40",
    calendly_events: 18,
    calendly_com_events: 10,
    conversions: 8
  },
  {
    account_id: "91396948",
    ad_name: "Luxury Yorkville Homes",
    ad_id: "6817650353533",
    campaign_id: "6769443126533",
    campaign_name: "AD_CA_Yorkville_Luxury_11.05.2025",
    adset_id: "6817650353733",
    adset_name: "High Net Worth 40-65",
    date_start: "2025-11-05",
    date_stop: "2025-11-11",
    created_time: "2025-11-01",
    cpc: "5.65",
    cpm: "68.20",
    cpp: "72.15",
    ctr: "0.83",
    frequency: "1.18",
    impressions: "1450",
    spend: "510.30",
    calendly_events: 12,
    calendly_com_events: 8,
    conversions: 6
  },
  {
    account_id: "91396949",
    ad_name: "First-Time Buyer Webinar",
    ad_id: "6817650353534",
    campaign_id: "6769443126534",
    campaign_name: "AD_CA_FirstTimeBuyer_Education_10.25.2025",
    adset_id: "6817650353734",
    adset_name: "Lookalike - Recent Inquiries",
    date_start: "2025-10-25",
    date_stop: "2025-11-08",
    created_time: "2025-10-22",
    cpc: "1.95",
    cpm: "28.50",
    cpp: "30.25",
    ctr: "0.92",
    frequency: "1.35",
    impressions: "4120",
    spend: "380.25",
    calendly_events: 45,
    calendly_com_events: 28,
    conversions: 24
  },
  {
    account_id: "91396950",
    ad_name: "North York Family Homes",
    ad_id: "6817650353535",
    campaign_id: "6769443126535",
    campaign_name: "AD_CA_NorthYork_FamilyHomes_11.11.2025",
    adset_id: "6817650353735",
    adset_name: "Parents 30-50 Toronto",
    date_start: "2025-11-11",
    date_stop: "2025-11-11",
    created_time: "2025-11-08",
    cpc: "2.95",
    cpm: "39.80",
    cpp: "42.15",
    ctr: "0.74",
    frequency: "1.20",
    impressions: "3180",
    spend: "325.90",
    calendly_events: 28,
    calendly_com_events: 16,
    conversions: 15
  },
  {
    account_id: "91396951",
    ad_name: "Pre-Construction Vaughan",
    ad_id: "6817650353536",
    campaign_id: "6769443126536",
    campaign_name: "AD_CA_Vaughan_PreConstruction_Investment_11.11.2025",
    adset_id: "6817650353736",
    adset_name: "High Income 35-55 Investors",
    date_start: "2025-11-11",
    date_stop: "2025-11-11",
    created_time: "2025-11-09",
    cpc: "4.15",
    cpm: "52.30",
    cpp: "55.80",
    ctr: "0.79",
    frequency: "1.12",
    impressions: "1890",
    spend: "445.20",
    calendly_events: 18,
    calendly_com_events: 12,
    conversions: 9
  },
  {
    account_id: "91396952",
    ad_name: "Oakville Luxury Estates",
    ad_id: "6817650353537",
    campaign_id: "6769443126537",
    campaign_name: "AD_CA_Oakville_LuxuryEstates_11.11.2025",
    adset_id: "6817650353737",
    adset_name: "High Net Worth - Waterfront",
    date_start: "2025-11-11",
    date_stop: "2025-11-11",
    created_time: "2025-11-10",
    cpc: "6.25",
    cpm: "78.40",
    cpp: "82.90",
    ctr: "0.80",
    frequency: "1.08",
    impressions: "1240",
    spend: "465.00",
    calendly_events: 14,
    calendly_com_events: 9,
    conversions: 7
  },
  {
    account_id: "91396953",
    ad_name: "Markham Townhomes",
    ad_id: "6817650353538",
    campaign_id: "6769443126538",
    campaign_name: "AD_CA_Markham_Townhomes_NewDevelopment_11.11.2025",
    adset_id: "6817650353738",
    adset_name: "Interest - New Developments",
    date_start: "2025-11-11",
    date_stop: "2025-11-11",
    created_time: "2025-11-09",
    cpc: "2.65",
    cpm: "36.20",
    cpp: "38.50",
    ctr: "0.73",
    frequency: "1.25",
    impressions: "2940",
    spend: "285.40",
    calendly_events: 22,
    calendly_com_events: 13,
    conversions: 11
  },
  {
    account_id: "91396954",
    ad_name: "Downtown Rental Properties",
    ad_id: "6817650353539",
    campaign_id: "6769443126539",
    campaign_name: "AD_CA_Toronto_Downtown_Rentals_11.11.2025",
    adset_id: "6817650353739",
    adset_name: "Retargeting - Lead Magnets",
    date_start: "2025-11-11",
    date_stop: "2025-11-11",
    created_time: "2025-11-10",
    cpc: "1.85",
    cpm: "26.80",
    cpp: "28.90",
    ctr: "0.69",
    frequency: "1.42",
    impressions: "3680",
    spend: "165.60",
    calendly_events: 35,
    calendly_com_events: 20,
    conversions: 18
  }
];

// Simulate network delay
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Helper function for checking custom ID availability
const checkCustomIdAvailability = async (
  customId: string
): Promise<boolean> => {
  await delay(300);

  // Check if the custom ID is already taken
  const normalizedId = customId.toLowerCase().trim();
  const isTaken = mockLinks.some(
    (link) => link.customId && link.customId.toLowerCase() === normalizedId
  );

  return !isTaken;
};

// Mock API functions
export const mockApi = {
  login: async (data: LoginData): Promise<User> => {
    await delay(1000);

    // Simple mock authentication
    // Simple mock authentication - always succeed for demo
    return {
      id: 1,
      email: data.email,
      name: "Admin User",
      token: "mock-jwt-token-12345"
    };
  },

  getLinks: async (): Promise<Link[]> => {
    await delay(800);
    return [...mockLinks];
  },

  getLink: async (id: string): Promise<Link> => {
    await delay(600);
    const link = mockLinks.find((c) => c.id === id);
    if (!link) {
      throw new Error("Link not found");
    }
    return { ...link };
  },

  checkCustomIdAvailability,

  createLink: async (data: CreateLinkData): Promise<Link> => {
    await delay(1000);

    // If custom ID is provided, check if it's available
    if (data.customId) {
      const isAvailable = await checkCustomIdAvailability(data.customId);
      if (!isAvailable) {
        throw new Error(
          `The custom ID "${data.customId}" is already taken. Please choose another.`
        );
      }
    }

    const newLink: Link = {
      id: `camp_${Date.now()}`,
      customId: data.customId || null,
      created: new Date(),
      updated: null,
      redirectUrl: data.redirectUrl,
      platform: data.platform,
      linkName: data.linkName ?? null,
      adSetName: data.adSetName ?? null,
      adName: data.adName ?? null,
      metadata: data.metadata || null
    };

    mockLinks.push(newLink);
    return { ...newLink };
  },

  updateLink: async (
    id: string,
    data: Partial<CreateLinkData>
  ): Promise<Link> => {
    await delay(800);

    const linkIndex = mockLinks.findIndex((c) => c.id === id);
    if (linkIndex === -1) {
      throw new Error("Link not found");
    }

    // If updating custom ID, check if it's available
    if (data.customId && data.customId !== mockLinks[linkIndex].customId) {
      const isAvailable = await checkCustomIdAvailability(data.customId);
      if (!isAvailable) {
        throw new Error(
          `The custom ID "${data.customId}" is already taken. Please choose another.`
        );
      }
    }

    const updatedLink = {
      ...mockLinks[linkIndex],
      ...data,
      updated: new Date()
    };

    mockLinks[linkIndex] = updatedLink;
    return { ...updatedLink };
  },

  deleteLink: async (id: string): Promise<void> => {
    await delay(600);

    const linkIndex = mockLinks.findIndex((c) => c.id === id);
    if (linkIndex === -1) {
      throw new Error("Link not found");
    }

    mockLinks.splice(linkIndex, 1);
  },

  // Campaign API functions
  getCampaigns: async (params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<Campaign[]> => {
    await delay(800);

    let filteredCampaigns = [...mockCampaigns];

    // Filter by date range if provided
    if (params?.startDate && params?.endDate) {
      const startDate = new Date(params.startDate);
      const endDate = new Date(params.endDate);

      filteredCampaigns = filteredCampaigns.filter((campaign) => {
        if (!campaign.date_start) return true; // Include campaigns without start date

        const campaignStartDate = new Date(campaign.date_start);
        return campaignStartDate >= startDate && campaignStartDate <= endDate;
      });
    }

    return filteredCampaigns;
  },

  getCampaign: async (id: string): Promise<Campaign> => {
    await delay(600);
    const campaign = mockCampaigns.find((c) => c.campaign_id === id);
    if (!campaign) {
      throw new Error("Campaign not found");
    }
    return { ...campaign };
  },

  createCampaign: async (data: CreateCampaignData): Promise<Campaign> => {
    await delay(800);

    const newCampaign: Campaign = {
      account_id: `acc_${Date.now()}`,
      ad_name: data.adName || null,
      ad_id: `ad_${Date.now()}`,
      campaign_id: `camp_${Date.now()}`,
      campaign_name: data.campaignName || null,
      adset_id: `adset_${Date.now()}`,
      adset_name: data.adSetName || null,
      date_start: new Date().toISOString(),
      date_stop: null,
      created_time: new Date().toISOString(),
      cpc: null,
      cpm: null,
      cpp: null,
      ctr: null,
      frequency: null,
      impressions: null,
      spend: null
    };

    mockCampaigns.push(newCampaign);
    return { ...newCampaign };
  },

  updateCampaign: async (
    id: string,
    data: Partial<{
      campaignName: string;
      adSetName: string;
      adName: string;
    }>
  ): Promise<Campaign> => {
    await delay(800);

    const campaignIndex = mockCampaigns.findIndex((c) => c.campaign_id === id);
    if (campaignIndex === -1) {
      throw new Error("Campaign not found");
    }

    const updatedCampaign = {
      ...mockCampaigns[campaignIndex],
      campaign_name:
        data.campaignName || mockCampaigns[campaignIndex].campaign_name,
      adset_name: data.adSetName || mockCampaigns[campaignIndex].adset_name,
      ad_name: data.adName || mockCampaigns[campaignIndex].ad_name
    };

    mockCampaigns[campaignIndex] = updatedCampaign;
    return { ...updatedCampaign };
  },

  deleteCampaign: async (id: string): Promise<void> => {
    await delay(600);

    const campaignIndex = mockCampaigns.findIndex((c) => c.campaign_id === id);
    if (campaignIndex === -1) {
      throw new Error("Campaign not found");
    }

    mockCampaigns.splice(campaignIndex, 1);
  },

  // A/B Test API functions
  getABTests: async (): Promise<ABTest[]> => {
    await delay(800);
    return [...mockABTests];
  },

  getABTest: async (id: string): Promise<ABTest> => {
    await delay(600);
    const test = mockABTests.find((t) => t.id === id);
    if (!test) {
      throw new Error("A/B Test not found");
    }
    return { ...test };
  },

  createABTest: async (data: Partial<ABTest>): Promise<ABTest> => {
    await delay(1000);

    const newTest: ABTest = {
      id: `test_${Date.now()}`,
      name: data.name || "New A/B Test",
      status: data.status || "draft",
      variants: data.variants || 2,
      trafficSplit: data.trafficSplit || [
        { name: "Control", percentage: 50 },
        { name: "Variant A", percentage: 50 }
      ],
      conversionRate: 0,
      totalVisitors: 0,
      created: new Date(),
      updated: null,
      isActive: data.status === "active",
      isWinner: false
    };

    mockABTests.push(newTest);
    return { ...newTest };
  },

  updateABTest: async (id: string, data: Partial<ABTest>): Promise<ABTest> => {
    await delay(800);

    const testIndex = mockABTests.findIndex((t) => t.id === id);
    if (testIndex === -1) {
      throw new Error("A/B Test not found");
    }

    const updatedTest = {
      ...mockABTests[testIndex],
      ...data,
      updated: new Date()
    };

    mockABTests[testIndex] = updatedTest;
    return { ...updatedTest };
  },

  deleteABTest: async (id: string): Promise<void> => {
    await delay(600);

    const testIndex = mockABTests.findIndex((t) => t.id === id);
    if (testIndex === -1) {
      throw new Error("A/B Test not found");
    }

    mockABTests.splice(testIndex, 1);
  },

  // Path API functions
  getPaths: async (): Promise<Path[]> => {
    await delay(800);
    return [...mockPaths];
  },

  getPath: async (id: string): Promise<Path> => {
    await delay(600);
    const path = mockPaths.find((p) => p.id === id);
    if (!path) {
      throw new Error("Path not found");
    }
    return { ...path };
  },

  createPath: async (data: Partial<Path>): Promise<Path> => {
    await delay(1000);

    const newPath: Path = {
      id: `vpath_${Date.now()}`,
      path: data.path || "/new-path",
      githubRepo: data.githubRepo || "example/default-repo",
      githubSubfolder: data.githubSubfolder,
      githubTag: data.githubTag || "main",
      targetUrl: data.targetUrl,
      status: data.status || "inactive",
      requestCount: 0,
      lastAccessed: null,
      created: new Date(),
      updated: null,
      description: data.description,
      metadata: data.metadata,
      forceExternalBrowser: data.forceExternalBrowser || false,
      customQueryParams: data.customQueryParams,
      parameters: data.parameters || []
    };

    mockPaths.push(newPath);
    return { ...newPath };
  },

  updatePath: async (id: string, data: Partial<Path>): Promise<Path> => {
    await delay(800);

    const pathIndex = mockPaths.findIndex((p) => p.id === id);
    if (pathIndex === -1) {
      throw new Error("Path not found");
    }

    const updatedPath = {
      ...mockPaths[pathIndex],
      ...data,
      updated: new Date()
    };

    mockPaths[pathIndex] = updatedPath;
    return { ...updatedPath };
  },

  deletePath: async (id: string): Promise<void> => {
    await delay(600);

    const pathIndex = mockPaths.findIndex((p) => p.id === id);
    if (pathIndex === -1) {
      throw new Error("Path not found");
    }

    mockPaths.splice(pathIndex, 1);
  },

  // Path validation functions
  checkPathAvailability: async (path: string): Promise<{ available: boolean; message?: string }> => {
    await delay(300);
    
    // Check if path format is valid
    if (!path.startsWith("/")) {
      return {
        available: false,
        message: "Path must start with /"
      };
    }
    
    // Check for invalid characters
    const invalidChars = /[^a-zA-Z0-9\-_\/]/;
    if (invalidChars.test(path)) {
      return {
        available: false,
        message: "Path contains invalid characters. Use only letters, numbers, hyphens, underscores, and forward slashes"
      };
    }
    
    // Check if path already exists
    const normalizedPath = path.toLowerCase().trim();
    const pathExists = mockPaths.some(
      (p) => p.path.toLowerCase() === normalizedPath
    );
    
    if (pathExists) {
      return {
        available: false,
        message: "This path is already in use"
      };
    }
    
    // Check for reserved paths
    const reservedPaths = [
      "/admin",
      "/api",
      "/auth",
      "/login",
      "/logout",
      "/register",
      "/dashboard",
      "/settings"
    ];
    
    if (reservedPaths.includes(normalizedPath)) {
      return {
        available: false,
        message: "This is a reserved system path"
      };
    }
    
    return { available: true };
  },

  validatePath: async (path: string): Promise<{ valid: boolean; message?: string }> => {
    await delay(200);
    
    if (!path) {
      return {
        valid: false,
        message: "Path is required"
      };
    }
    
    if (!path.startsWith("/")) {
      return {
        valid: false,
        message: "Path must start with /"
      };
    }
    
    if (path.length < 2) {
      return {
        valid: false,
        message: "Path must be at least 2 characters long"
      };
    }
    
    if (path.length > 100) {
      return {
        valid: false,
        message: "Path must be less than 100 characters"
      };
    }
    
    // Check for consecutive slashes
    if (path.includes("//")) {
      return {
        valid: false,
        message: "Path cannot contain consecutive slashes"
      };
    }
    
    // Check for invalid characters
    const invalidChars = /[^a-zA-Z0-9\-_\/]/;
    if (invalidChars.test(path)) {
      return {
        valid: false,
        message: "Path contains invalid characters"
      };
    }
    
    return { valid: true };
  },

  // GitHub validation functions
  validateGithubRepo: async (repo: string): Promise<{ valid: boolean; message?: string }> => {
    await delay(500);
    
    // Mock validation - check if repo format is valid (owner/repo)
    const repoPattern = /^[a-zA-Z0-9-]+\/[a-zA-Z0-9-_.]+$/;
    
    if (!repoPattern.test(repo)) {
      return {
        valid: false,
        message: "Invalid repository format. Use: owner/repository"
      };
    }
    
    // Mock list of valid repositories
    const validRepos = [
      "example/marketing-site",
      "example/webinar-platform",
      "example/documentation",
      "example/mobile-app",
      "example/partner-portal",
      "example/events-site",
      "example/support-center",
      "example/blog",
      "example/default-repo",
      "acme/frontend",
      "acme/backend",
      "myorg/website",
      "myorg/docs"
    ];
    
    if (!validRepos.includes(repo)) {
      return {
        valid: false,
        message: "Repository not found or you don't have access"
      };
    }
    
    return { valid: true };
  },

  getGithubRepoSubfolders: async (repo: string): Promise<string[]> => {
    await delay(600);
    
    // Mock subfolders based on repository
    const subfolderMap: Record<string, string[]> = {
      "example/marketing-site": [
        "campaigns",
        "campaigns/summer",
        "campaigns/winter",
        "landing-pages",
        "promos"
      ],
      "example/webinar-platform": [
        "registration",
        "events",
        "recordings"
      ],
      "example/documentation": [
        "api",
        "api/v1",
        "api/v2",
        "guides",
        "tutorials"
      ],
      "example/mobile-app": [
        "download",
        "releases",
        "beta"
      ],
      "example/partner-portal": [
        "affiliate",
        "resellers",
        "integrations"
      ],
      "example/events-site": [
        "conferences",
        "conferences/2024",
        "webinars",
        "workshops"
      ],
      "example/support-center": [
        "help",
        "faq",
        "tickets"
      ],
      "example/blog": [
        "posts",
        "posts/latest",
        "posts/archive",
        "categories"
      ],
      "acme/frontend": [
        "src",
        "src/pages",
        "src/components",
        "public"
      ],
      "acme/backend": [
        "api",
        "services",
        "models"
      ]
    };
    
    return subfolderMap[repo] || [];
  },

  getGithubRepoTags: async (_repo: string): Promise<string[]> => {
    await delay(500);
    
    // Mock tags for repositories
    return [
      "main",
      "latest",
      "stable",
      "v2.1.0",
      "v2.0.0",
      "v1.5.0",
      "v1.0.0",
      "beta",
      "alpha",
      "develop"
    ];
  },

  validateGithubSubfolder: async (repo: string, subfolder: string): Promise<{ valid: boolean; message?: string }> => {
    await delay(400);
    
    const subfolders = await mockApi.getGithubRepoSubfolders(repo);
    
    if (!subfolders.includes(subfolder)) {
      return {
        valid: false,
        message: "Subfolder not found in repository"
      };
    }
    
    return { valid: true };
  },

  // Scripts API
  getScripts: async (): Promise<any[]> => {
    await delay(500);
    return mockScripts;
  },

  getScript: async (id: string): Promise<any> => {
    await delay(300);
    const script = mockScripts.find((s: any) => s.id === id);
    if (!script) {
      throw new Error("Script not found");
    }
    return { ...script };
  },

  createScript: async (data: any): Promise<any> => {
    await delay(600);
    const newScript = {
      id: `script-${Date.now()}`,
      ...data,
      createdBy: "Admin User",
      lastModified: new Date().toLocaleDateString(),
      usageCount: 0
    };
    mockScripts.push(newScript);
    return newScript;
  },

  updateScript: async (id: string, data: any): Promise<any> => {
    await delay(600);
    const index = mockScripts.findIndex((s: any) => s.id === id);
    if (index === -1) {
      throw new Error("Script not found");
    }
    mockScripts[index] = {
      ...mockScripts[index],
      ...data,
      lastModified: new Date().toLocaleDateString()
    };
    return mockScripts[index];
  },

  // Preconnect Entry methods
  getPreconnectEntries: async (): Promise<PreconnectEntry[]> => {
    await delay(500);
    return [...mockPreconnectEntries];
  },

  getPreconnectEntry: async (id: string): Promise<PreconnectEntry> => {
    await delay(300);
    const entry = mockPreconnectEntries.find((e) => e.id === id);
    if (!entry) {
      throw new Error("Preconnect entry not found");
    }
    return entry;
  },

  createPreconnectEntry: async (data: Partial<PreconnectEntry>): Promise<PreconnectEntry> => {
    await delay(800);
    const newEntry: PreconnectEntry = {
      id: `preconnect_${Date.now()}`,
      url: data.url || "",
      type: data.type || "dns-prefetch",
      status: data.status || "active",
      description: data.description,
      paths: data.paths || [],
      created: new Date(),
      updated: null,
      lastUsed: null,
      usageCount: 0,
      performance: {
        avgTimeSaved: 0,
        successRate: 0
      }
    };
    mockPreconnectEntries.push(newEntry);
    return newEntry;
  },

  updatePreconnectEntry: async (id: string, data: Partial<PreconnectEntry>): Promise<PreconnectEntry> => {
    await delay(600);
    const index = mockPreconnectEntries.findIndex((e) => e.id === id);
    if (index === -1) {
      throw new Error("Preconnect entry not found");
    }
    mockPreconnectEntries[index] = {
      ...mockPreconnectEntries[index],
      ...data,
      updated: new Date()
    };
    return mockPreconnectEntries[index];
  },

  deletePreconnectEntry: async (id: string): Promise<void> => {
    await delay(500);
    const index = mockPreconnectEntries.findIndex((e) => e.id === id);
    if (index === -1) {
      throw new Error("Preconnect entry not found");
    }
    mockPreconnectEntries.splice(index, 1);
  },

  deleteScript: async (id: string): Promise<void> => {
    await delay(400);
    const index = mockScripts.findIndex((s: any) => s.id === id);
    if (index === -1) {
      throw new Error("Script not found");
    }
    mockScripts.splice(index, 1);
  }
};
